import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import { delay } from "../helper/delayresponse";

interface BlockList {
  id?: number;
  number?: string;
  blocklist_numbers_campaigns:{
    sms_campaign:{
      name:string
    }
    block_by?: number;
    user?: {
      name: string;
      email: string;
    } | null;
  }[]
  createdAt?: string;
  updatedAt?: string;
}

interface CreateBlockList {
  id?: number;
  number?: string;
  campaign_id?: number[]
}

interface BlockListState {
  blocklistdata: BlockList[];
  info: {
    count: number;
    current_page: number;
    limit: number;
    total_page: number;
  } | null;
  error: string | null;
  loading: boolean;
}

//==================get-api-action-start=============
export const GetAllBlockList = createAsyncThunk<
  {
    data: BlockList[];
    info: {
      count: number;
      current_page: number;
      limit: number;
      total_page: number;
    };
  },
  { search: string; page: number; limit: number },
  { rejectValue: string }
>("GetAllBlockList", async ({ search, page, limit }, { rejectWithValue }) => {
  try {
    await delay(1000);
    const response = await axiosInstance.get(`/api/admin/blocklist`, {
      params: { search, page, limit },
    });
    return { data: response.data.data, info: response.data.info };
  } catch (error: any) {
    if (error.response && error.response.data) {
      return rejectWithValue(error.response.data.message || "Request failed");
    }
    return rejectWithValue(error.message || "Unknown error");
  }
});
//==================get-api-action-end=============

//==================post-api-action-start=============
export const AddBlockList = createAsyncThunk<
  BlockList,
  CreateBlockList,
  { rejectValue: string }
>("blocklistdata/AddBlockList", async (allowipData, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(
      `/api/admin/blocklist/create`,
      allowipData
    );
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      return rejectWithValue(error.response?.data);
    }
    return rejectWithValue(error.message);
  }
});
//==================post-api-action-end=============

// =================Update-action-start=========
export const UpdateBlockList = createAsyncThunk<
  BlockList,
  { id: number; blocklistData: CreateBlockList },
  { rejectValue: string }
>(
  "blocklistdata/UpdateBlockList",
  async ({ id, blocklistData }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/api/admin/blocklist/update/${id}`,
        blocklistData
      );
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(error.message);
    }
  }
);
// =================Update-action-end=========

// =================Delete-api-action-start===========
export const DeleteBlockList = createAsyncThunk<{ id: number }, { id: number }>(
  "blocklistdata/DeleteBlockList",
  async ({ id }) => {
    try {
      const { data } = await axiosInstance.delete(`/api/admin/blocklist/${id}`);
      return data;
    } catch (error: any) {
      if (error.response && error.response.data) {
        return error.response.data;
      }
      return error.message;
    }
  }
);
// =================Delete-api-action-end===========

const initialState: BlockListState = {
  blocklistdata: [],
  info: null,
  error: null,
  loading: false,
};

const blocklistSlice = createSlice({
  name: "allowIp",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ==========get-api-addcase-start==========
      .addCase(GetAllBlockList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        GetAllBlockList.fulfilled,
        (
          state,
          action: PayloadAction<{
            data: BlockList[];
            info: {
              count: number;
              current_page: number;
              limit: number;
              total_page: number;
            };
          }>
        ) => {
          state.loading = false;
          state.blocklistdata = action.payload.data;
          state.info = action.payload.info;
        }
      )
      .addCase(
        GetAllBlockList.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "An error occurred";
        }
      );
    // ==========get-api-addcase-end==========

    // ==========post-api-addcase-start==========
    builder
      .addCase(AddBlockList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        AddBlockList.fulfilled,
        (state, action: PayloadAction<BlockList>) => {
          state.loading = false;
          state.blocklistdata.push(action.payload);
        }
      )
      .addCase(
        AddBlockList.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to add Block-list";
        }
      );
    // ==========post-api-addcase-end==========

    // =================Update-slice-start============
    builder
      .addCase(UpdateBlockList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        UpdateBlockList.fulfilled,
        (state, action: PayloadAction<BlockList>) => {
          state.loading = false;
          const index = state.blocklistdata.findIndex(
            (item) => item.id === action.payload.id
          );
          if (index !== -1) {
            state.blocklistdata[index] = action.payload;
          }
        }
      )
      .addCase(
        UpdateBlockList.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update Block-list";
        }
      );
    // =================Update-slice-start============

    // ================Delete-api-start=========
    builder
      .addCase(DeleteBlockList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        DeleteBlockList.fulfilled,
        (state, action: PayloadAction<{ id: number }>) => {
          state.loading = false;
          state.blocklistdata = state.blocklistdata.filter(
            (item) => item.id !== action.payload.id
          );
        }
      )
      .addCase(DeleteBlockList.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete Block-list";
      });
    // ================Delete-api-end=========
  },
});

export default blocklistSlice.reducer;
